import React from "react";
import { X, Copy, RefreshCw, Globe2, ExternalLink, BookOpen, GitBranch, Split, Sparkles, UserCheck, AlertCircle, Users, Compass, Award, AlertTriangle, Lightbulb, Layers, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
import SelfArt from "./SelfArt";
import { LifeSwitcher, LifeGlanceCard, ChaptersTab, EarthComparisonTab, DayInLifeTab, FuturesAndTimelineTab } from "./life";
import type { AlternateSelf, AxisId } from "@/lib/infiniteSelves";
import { AXIS_ORDER, AXIS_LABELS, FICTION_NOTICE, universeLinkFor } from "@/lib/infiniteSelves";
import { ENGINE_VERSION } from "@/lib/infiniteSelves/lifeChapters";
import { HABITABILITY_NOTE, type UniverseConfig } from "@/lib/universe";
export interface SelfDetailProps {
  self: AlternateSelf;
  others: AlternateSelf[];
  nickname: string;
  onClose: () => void;
  onRegenerate: () => void;
  onCopy: () => void;
  onPlaceAllInUniverse: (config: UniverseConfig) => void;
  onBridge?: () => void;
  // Batch spec new props:
  allSelves?: AlternateSelf[];
  activeIndex?: number;
  onSwitchSelf?: (index: number) => void;
  variationId?: string;
}
export default function SelfDetail(props: SelfDetailProps): JSX.Element {
  const {
    self,
    others,
    nickname,
    onClose,
    onRegenerate,
    onCopy,
    onPlaceAllInUniverse,
    onBridge,
    allSelves,
    activeIndex,
    onSwitchSelf,
    variationId
  } = props;
  const {
    scenarioLabel,
    name,
    title,
    timelineId,
    tagline,
    occupation,
    traits,
    axes,
    story,
    choices,
    worldDetails,
    universe,
    differences,
    visual,
    lifeGlance,
    life
  } = self;
  const cardHue = visual?.hue ?? 186;

  // Compute a list of all selves if not explicitly passed
  const resolvedAllSelves = React.useMemo(() => {
    if (allSelves && allSelves.length > 0) return allSelves;
    return [self, ...others];
  }, [allSelves, self, others]);
  const resolvedActiveIndex = React.useMemo(() => {
    if (typeof activeIndex === "number" && activeIndex >= 0) return activeIndex;
    const found = resolvedAllSelves.findIndex(s => s.timelineId === self.timelineId);
    return found >= 0 ? found : 0;
  }, [activeIndex, resolvedAllSelves, self.timelineId]);
  return <div className="ql-glass-strong relative overflow-hidden rounded-2xl border border-primary/40 p-4 sm:p-6 md:p-9 shadow-2xl space-y-6 sm:space-y-7 w-full max-w-full">
      {/* Top Coordinate Horizon Glow */}
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{
      background: `linear-gradient(90deg, transparent, hsl(${cardHue} 100% 55%), hsl(${visual.hue2} 90% 50%), transparent)`
    }} />

      {/* Life Switcher at the top of SelfDetail */}
      {onSwitchSelf && resolvedAllSelves.length > 1 && <LifeSwitcher allSelves={resolvedAllSelves} activeIndex={resolvedActiveIndex} onSwitchSelf={onSwitchSelf} cardHue={cardHue} />}

      {/* Header with Close Button and Engine Version */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-5">
        <div className="space-y-1.5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-mono font-bold uppercase tracking-wider text-primary">
              {scenarioLabel}
            </span>
            <span className="text-muted-foreground">·</span>
            <Badge variant="outline" className="text-[10px] font-mono tracking-wider border-primary/40 text-primary bg-primary/10 shadow-[0_0_8px_var(--glow-primary)]">
              {timelineId}
            </Badge>

            {/* Variation ID & Engine Version Badge */}
            <Badge variant="outline" className="text-[10px] font-mono tracking-wider border-border/80 bg-muted/60 text-muted-foreground">
              {variationId ? `Variation ${variationId} · engine ${ENGINE_VERSION}` : `engine ${ENGINE_VERSION}`}
            </Badge>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground break-words">
            {name}
          </h2>
          <p className="font-sans text-sm sm:text-base text-foreground/80 italic break-words">
            {title}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto shrink-0">
          {onBridge && <button type="button" onClick={onBridge} className="ql-btn-ghost min-h-[44px] text-xs" aria-label={`Bridge ${name} to reality in the Dream-to-Reality Bridge`}>
              <Sparkles className="size-3.5 mr-1.5 text-primary shrink-0" />
              <span>Bridge this self to reality</span>
            </button>}

          <button type="button" onClick={onClose} className="ql-btn-ghost min-h-[44px] text-xs" aria-label="Close self details">
            <X className="size-4 mr-1.5 shrink-0" />
            <span>Close View</span>
          </button>
        </div>
      </div>

      {/* Hero Visualizer Banner */}
      <div className="space-y-2">
        <div className="relative group/hero rounded-xl overflow-hidden border border-border shadow-2xl">
          <SelfArt self={self} variant="hero" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 ring-1 ring-primary/30 shadow-[inset_0_0_30px_var(--glow-primary)]" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground pt-1 px-1">
          <span className="italic">"{tagline}"</span>
          <span className="font-mono text-primary font-semibold">
            {occupation}
          </span>
        </div>
      </div>

      {/* Tabs Container */}
      <Tabs defaultValue="story" className="w-full space-y-6">
        {/* Horizontally scrollable on mobile to avoid overflow */}
        <div className="w-full overflow-x-auto pb-1.5 scrollbar-thin scrollbar-thumb-border/60" tabIndex={0} aria-label="Profile navigation sections">
          <TabsList className="inline-flex w-auto min-w-full sm:w-full bg-muted/60 p-1.5 rounded-xl border border-border h-auto min-h-[44px] gap-1 flex-nowrap" aria-label="Self profile tabs">
            <TabsTrigger value="story" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <BookOpen className="size-3.5 mr-1.5 shrink-0" />
              <span>Story & Traits</span>
            </TabsTrigger>

            {self.dossier && <TabsTrigger value="dossier" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
                <UserCheck className="size-3.5 mr-1.5 shrink-0" />
                <span>Full Dossier</span>
              </TabsTrigger>}

            {/* NEW LIFE RECORD TAB: Chapters */}
            <TabsTrigger value="life-chapters" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Layers className="size-3.5 mr-1.5 shrink-0 text-primary" />
              <span>Life Chapters</span>
            </TabsTrigger>

            {/* NEW LIFE RECORD TAB: Earth Comparison */}
            <TabsTrigger value="life-earth" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Split className="size-3.5 mr-1.5 shrink-0 text-chart-3" />
              <span>Earth Comparison</span>
            </TabsTrigger>

            {/* NEW LIFE RECORD TAB: A Day in This Life */}
            <TabsTrigger value="life-day" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Clock className="size-3.5 mr-1.5 shrink-0" />
              <span>A Day in Life</span>
            </TabsTrigger>

            {/* NEW LIFE RECORD TAB: Futures & Timeline */}
            <TabsTrigger value="life-futures" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Compass className="size-3.5 mr-1.5 shrink-0" />
              <span>Futures & Timeline</span>
            </TabsTrigger>

            <TabsTrigger value="choices" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <GitBranch className="size-3.5 mr-1.5 shrink-0" />
              <span>Life Choices</span>
            </TabsTrigger>

            <TabsTrigger value="universe" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Globe2 className="size-3.5 mr-1.5 shrink-0" />
              <span>Universe</span>
            </TabsTrigger>

            <TabsTrigger value="differences" className="min-h-[44px] text-xs font-mono data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-md px-3 py-2 shrink-0 flex items-center justify-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:outline-none">
              <Split className="size-3.5 mr-1.5 shrink-0" />
              <span>Differences</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* TAB 1: STORY & TRAITS */}
        <TabsContent value="story" className="space-y-6 focus-visible:outline-none">
          {/* Life at a Glance Free Banner Card */}
          {lifeGlance && <LifeGlanceCard glance={lifeGlance} cardHue={cardHue} titlePrefix={name} />}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Story Beats */}
            <div className="lg:col-span-7 space-y-4">
              <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Sparkles className="size-3 text-primary" />
                <span>Timeline Biography</span>
              </h3>

              {story.origin && <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2 shadow-sm">
                  <span className="text-[11px] font-mono uppercase font-bold text-primary block">
                    Origin
                  </span>
                  <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                    {story.origin}
                  </p>
                </div>}

              {story.turningPoint ? <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2 shadow-sm">
                  <span className="text-[11px] font-mono uppercase font-bold text-chart-3 block">
                    Turning Point
                  </span>
                  <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                    {story.turningPoint}
                  </p>
                </div> : null}

              {story.today && <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2 shadow-sm">
                  <span className="text-[11px] font-mono uppercase font-bold text-primary block">
                    Today
                  </span>
                  <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                    {story.today}
                  </p>
                </div>}
            </div>

            {/* Exactly 4 Traits Meters */}
            <div className="lg:col-span-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Core Character Traits
                </h3>
                <span className="text-[10px] font-mono text-primary font-semibold">
                  {traits.length} PROFILES
                </span>
              </div>

              <div className="space-y-3">
                {traits.map((t, idx) => <div key={idx} className="p-3.5 rounded-xl bg-card border border-border/80 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-foreground font-sans">
                        {t.name}
                      </span>
                      <span className="font-mono text-primary font-bold">
                        {t.level}%
                      </span>
                    </div>
                    <Progress value={t.level} className="h-2 bg-muted/80" />
                    <p className="text-[11px] text-muted-foreground leading-normal">
                      {t.description}
                    </p>
                  </div>)}
              </div>

              {/* Occupation Callout */}
              {occupation && <div className="p-3.5 rounded-xl border border-primary/30 bg-primary/10 space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-primary block">
                    Daily Vocation
                  </span>
                  <p className="text-xs sm:text-sm font-semibold text-foreground">
                    {occupation}
                  </p>
                </div>}

              {/* Defining Dilemma Highlight on Story Tab */}
              {self.dossier?.dilemma ? <div className="p-4 rounded-xl border-2 border-chart-3/60 bg-chart-3/10 space-y-2">
                  <div className="flex items-center gap-1.5">
                    <AlertCircle className="size-3.5 text-chart-3 shrink-0" />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-chart-3 font-bold">
                      Defining Dilemma
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-foreground/95 leading-relaxed font-sans">
                    {self.dossier.dilemma}
                  </p>
                </div> : null}
            </div>
          </div>
        </TabsContent>

        {/* TAB: FULL DOSSIER */}
        {self.dossier && <TabsContent value="dossier" className="space-y-6 focus-visible:outline-none">
            {/* Top row: Defining Dilemma Emphasized Banner */}
            {self.dossier.dilemma ? <div className="p-5 rounded-xl border-2 border-primary/70 bg-primary/10 shadow-[0_0_24px_var(--glow-primary)] space-y-2">
                <div className="flex items-center justify-between gap-2 border-b border-primary/30 pb-2">
                  <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold flex items-center gap-2">
                    <AlertCircle className="size-4 text-primary shrink-0" />
                    <span>Defining Dilemma</span>
                  </span>
                  <Badge variant="outline" className="text-[10px] font-mono border-primary/50 text-primary">
                    Central Conflict
                  </Badge>
                </div>
                <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
                  {self.dossier.dilemma}
                </p>
              </div> : null}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Background, Personality, Motivations */}
              <div className="lg:col-span-7 space-y-5">
                {/* Background Section */}
                {self.dossier.background && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-2 shadow-sm">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
                      <Sparkles className="size-3.5 text-primary shrink-0" />
                      <span>Formative Background</span>
                    </span>
                    <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                      {self.dossier.background}
                    </p>
                  </div>}

                {/* Personality Section */}
                {self.dossier.personality && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-2 shadow-sm">
                    <span className="text-xs font-mono uppercase tracking-wider text-chart-3 font-bold flex items-center gap-2">
                      <UserCheck className="size-3.5 text-chart-3 shrink-0" />
                      <span>Personality & Demeanor</span>
                    </span>
                    <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                      {self.dossier.personality}
                    </p>
                  </div>}

                {/* Left Column Sections (Motivations, Achievements/Failures, Trajectory) */}
                {/* Motivations */}
                {self.dossier.motivations && self.dossier.motivations.length > 0 && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-3 shadow-sm">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
                      <Compass className="size-3.5 text-primary shrink-0" />
                      <span>Core Motivations & Driving Force</span>
                    </span>
                    <ul className="space-y-2 text-xs sm:text-sm text-foreground/90 font-sans">
                      {self.dossier.motivations.map((mot, idx) => <li key={idx} className="flex items-start gap-2.5">
                          <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                          <span className="leading-relaxed">{mot}</span>
                        </li>)}
                    </ul>
                  </div>}

                {/* Achievements & Failures (Duality) */}
                {self.dossier.achievements?.length || self.dossier.failures?.length ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Achievements */}
                    {self.dossier.achievements && self.dossier.achievements.length > 0 && <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2.5 shadow-sm">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-1.5">
                          <Award className="size-3.5 text-primary shrink-0" />
                          <span>Achievements</span>
                        </span>
                        <ul className="space-y-2 text-xs text-foreground/90 font-sans">
                          {self.dossier.achievements.map((ach, idx) => <li key={idx} className="flex items-start gap-2">
                              <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                              <span className="leading-relaxed">{ach}</span>
                            </li>)}
                        </ul>
                      </div>}

                    {/* Failures */}
                    {self.dossier.failures && self.dossier.failures.length > 0 && <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2.5 shadow-sm">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-destructive font-bold flex items-center gap-1.5">
                          <AlertTriangle className="size-3.5 text-destructive shrink-0" />
                          <span>Failures & Scars</span>
                        </span>
                        <ul className="space-y-2 text-xs text-foreground/90 font-sans">
                          {self.dossier.failures.map((fail, idx) => <li key={idx} className="flex items-start gap-2">
                              <span className="size-1.5 rounded-full bg-destructive mt-1.5 shrink-0" />
                              <span className="leading-relaxed">{fail}</span>
                            </li>)}
                        </ul>
                      </div>}
                  </div> : null}

                {/* Trajectory */}
                {self.dossier.trajectory && (self.dossier.trajectory.nearFuture || self.dossier.trajectory.longArc) && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-3 shadow-sm">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
                      <Compass className="size-3.5 text-primary shrink-0" />
                      <span>Life Trajectory & Horizon</span>
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      {self.dossier.trajectory.nearFuture && <div className="p-3 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                          <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                            Near Future
                          </span>
                          <p className="text-foreground/90 leading-relaxed font-sans">
                            {self.dossier.trajectory.nearFuture}
                          </p>
                        </div>}
                      {self.dossier.trajectory.longArc && <div className="p-3 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                          <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                            Long Arc
                          </span>
                          <p className="text-foreground/90 leading-relaxed font-sans">
                            {self.dossier.trajectory.longArc}
                          </p>
                        </div>}
                    </div>
                  </div>}
              </div>

              {/* Right Column: Skills, Relationships, Influence */}
              <div className="lg:col-span-5 space-y-5">
                {/* Skills as chips */}
                {self.dossier.skills && self.dossier.skills.length > 0 && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold">
                        Acquired Skills
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {self.dossier.skills.length} EXPERTISES
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {self.dossier.skills.map((skill, idx) => <span key={idx} className="ql-chip text-xs font-sans text-foreground/90 border-primary/30 bg-muted/60">
                          {skill}
                        </span>)}
                    </div>
                  </div>}

                {/* Key Relationships */}
                {self.dossier.relationships && self.dossier.relationships.length > 0 && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-3 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
                        <Users className="size-3.5 text-primary shrink-0" />
                        <span>Key Relationships</span>
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {self.dossier.relationships.length} BONDS
                      </span>
                    </div>
                    <div className="space-y-3">
                      {self.dossier.relationships.map((rel, idx) => <div key={idx} className="p-3 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-serif text-sm font-bold text-foreground">
                              {rel.who}
                            </span>
                            <Badge variant="outline" className="text-[10px] font-mono text-primary border-primary/40">
                              {rel.role}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                            {rel.bond}
                          </p>
                        </div>)}
                    </div>
                  </div>}

                {/* How your answers shaped this */}
                {self.dossier.influence && self.dossier.influence.length > 0 && <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 space-y-3 shadow-sm">
                    <span className="text-xs font-mono uppercase tracking-wider text-primary font-bold flex items-center gap-2">
                      <Lightbulb className="size-3.5 text-primary shrink-0" />
                      <span>How Your Answers Shaped This</span>
                    </span>
                    <ul className="space-y-2.5 text-xs text-foreground/90 font-sans">
                      {self.dossier.influence.map((inf, idx) => <li key={idx} className="flex items-start gap-2.5 p-2 rounded-lg bg-muted/30 border border-border/40">
                          <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                          <span className="leading-relaxed">{inf}</span>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </TabsContent>}

        {/* TAB: LIFE CHAPTERS */}
        <TabsContent value="life-chapters" className="space-y-6 focus-visible:outline-none">
          <ChaptersTab life={life} cardHue={cardHue} />
        </TabsContent>

        {/* TAB: EARTH COMPARISON */}
        <TabsContent value="life-earth" className="space-y-6 focus-visible:outline-none">
          <EarthComparisonTab life={life} cardHue={cardHue} />
        </TabsContent>

        {/* TAB: A DAY IN THIS LIFE */}
        <TabsContent value="life-day" className="space-y-6 focus-visible:outline-none">
          <DayInLifeTab life={life} cardHue={cardHue} />
        </TabsContent>

        {/* TAB: FUTURES & TIMELINE */}
        <TabsContent value="life-futures" className="space-y-6 focus-visible:outline-none">
          <FuturesAndTimelineTab life={life} cardHue={cardHue} />
        </TabsContent>

        {/* TAB 2: CHOICES */}
        <TabsContent value="choices" className="space-y-4 focus-visible:outline-none">
          <div className="space-y-1">
            <h3 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Divergence Decisions
            </h3>
            <p className="text-xs text-muted-foreground">
              Three critical junctures where reality branched from parallel possibilities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {choices.map((c, idx) => <div key={idx} className="p-4 rounded-xl bg-card border border-border flex flex-col justify-between space-y-4 shadow-sm">
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2">
                    <Badge variant="outline" className="text-[10px] font-mono text-chart-3 border-chart-3/40 bg-chart-3/10">
                      Epoch {c.age}
                    </Badge>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      Decision #{idx + 1}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[11px] font-mono uppercase font-bold text-primary block">
                      The Choice
                    </span>
                    <p className="text-xs text-foreground/90 leading-relaxed font-sans">
                      {c.choice}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[11px] font-mono uppercase font-bold text-muted-foreground block">
                      The Road Not Taken
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed italic font-sans">
                      "{c.roadNotTaken}"
                    </p>
                  </div>
                </div>

                <div className="border-t border-border/40 pt-3 space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/80 block">
                    What Followed
                  </span>
                  <p className="text-xs text-foreground/80 leading-relaxed font-sans">
                    {c.consequence}
                  </p>
                </div>
              </div>)}
          </div>
        </TabsContent>

        {/* TAB 3: UNIVERSE */}
        <TabsContent value="universe" className="space-y-6 focus-visible:outline-none">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Universe Overview & Model Stats */}
            <div className="lg:col-span-7 space-y-5">
              <div className="p-5 rounded-xl bg-card border border-border space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                      {universe.name}
                    </h3>
                    <p className="text-xs font-mono text-primary font-semibold">
                      Catalog Code: {universe.code}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[11px] font-mono uppercase border-primary/40 text-primary bg-primary/10 px-2.5 py-0.5">
                    Condition: {universe.condition}
                  </Badge>
                </div>

                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed">
                  {universe.description}
                </p>
              </div>

              {/* Habitability Index with Progress Bar */}
              <div className="p-4 rounded-xl bg-card border border-border space-y-3">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-muted-foreground uppercase font-semibold">
                    Habitability Index (model estimate)
                  </span>
                  <span className="font-bold text-foreground">
                    {universe.profile?.habitability ?? 50} / 100
                  </span>
                </div>
                <Progress value={universe.profile?.habitability ?? 50} className="h-2 bg-muted/80" />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-primary font-medium font-mono">
                    {universe.profile?.habitabilityLabel ?? universe.condition}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground border-t border-border/40 pt-2 leading-relaxed italic">
                  {HABITABILITY_NOTE}
                </p>
              </div>

              {/* Fate & Stage Diagnostic */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3.5 rounded-lg bg-card border border-border space-y-1">
                  <span className="text-[10px] font-mono uppercase text-muted-foreground block">
                    Ultimate Cosmological Fate
                  </span>
                  <span className="font-semibold text-foreground font-serif block capitalize">
                    {universe.profile?.fate ?? "Expanding Coastline"}
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {universe.profile?.fateText ?? "Long-term entropic evolution."}
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-card border border-border space-y-1">
                  <span className="text-[10px] font-mono uppercase text-muted-foreground block">
                    Current Evolutionary Stage
                  </span>
                  <span className="font-semibold text-foreground font-serif block capitalize">
                    {universe.profile?.stage ?? "Mature Stellar Era"}
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {universe.profile?.stageText ?? "Active star formation."}
                  </p>
                </div>
              </div>
            </div>

            {/* World Details Grid & Actions */}
            <div className="lg:col-span-5 space-y-5">
              <div className="space-y-3">
                <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Cosmic World Details
                </h4>
                <div className="grid grid-cols-1 gap-2 text-xs">
                  {worldDetails.map((wd, idx) => <div key={idx} className="p-3 rounded-lg bg-card border border-border flex items-start justify-between gap-3">
                      <span className="text-muted-foreground font-medium shrink-0">
                        {wd.label}
                      </span>
                      <span className="text-foreground text-right font-sans">
                        {wd.value}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Action Buttons: Open in Create Your Universe & Place All in Universe */}
              <div className="p-4 rounded-xl bg-card border border-border space-y-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground block">
                  Universe Exploration Controls
                </span>

                <Link to={universeLinkFor(universe.config)} className="ql-btn w-full min-h-[44px] inline-flex items-center justify-center text-xs px-4">
                  <ExternalLink className="size-3.5 mr-2 shrink-0" />
                  <span>Open in Create Your Universe</span>
                </Link>

                <button type="button" onClick={() => onPlaceAllInUniverse(universe.config)} className="ql-btn-ghost w-full min-h-[44px] text-xs">
                  <Globe2 className="size-3.5 mr-2 shrink-0" />
                  <span>Place all four selves in this universe</span>
                </button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TAB 4: DIFFERENCES */}
        <TabsContent value="differences" className="space-y-6 focus-visible:outline-none">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Divergence Summary & 3 Versus comparisons */}
              <div className="lg:col-span-7 space-y-5">
                {differences.summary && <div className="p-4 rounded-xl bg-card border border-border space-y-2">
                    <span className="text-[11px] font-mono uppercase font-bold text-primary block">
                      Unique Divergence Signature
                    </span>
                    <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-sans">
                      {differences.summary}
                    </p>
                  </div>}

                {/* Versus Entries */}
                {differences.versus && differences.versus.length > 0 && <div className="space-y-3">
                    <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      Contrasted Against Other Selves
                    </h4>
                    <div className="space-y-3">
                      {differences.versus.map((v, idx) => {
                  const otherSelf = others.find(o => o.archetype === v.other);
                  const headingLabel = otherSelf ? `${otherSelf.scenarioLabel} (${otherSelf.name})` : `Archetype: ${v.other}`;
                  return <div key={idx} className="p-3.5 rounded-lg bg-card border border-border space-y-1.5">
                            <span className="text-[11px] font-serif font-bold text-foreground block">
                              versus {headingLabel}
                            </span>
                            <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                              {v.text}
                            </p>
                          </div>;
                })}
                    </div>
                  </div>}
              </div>

              {/* Axis Comparison Bars */}
              <div className="lg:col-span-5 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    Dimensional Alignment Axes
                  </h4>
                  <p className="text-[11px] text-muted-foreground">
                    Calibrated profile values for {name}.
                  </p>
                </div>

                <div className="space-y-3 p-4 rounded-xl bg-card border border-border">
                  {AXIS_ORDER.map((axisKey: AxisId) => {
                const val = axes[axisKey] ?? 50;
                const label = AXIS_LABELS[axisKey] ?? axisKey;
                return <div key={axisKey} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-foreground/90 font-medium font-sans">
                            {label}
                          </span>
                          <span className="font-mono text-primary font-bold">
                            {val} / 100
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-muted/80 overflow-hidden border border-border/40">
                          <div className="h-full rounded-full transition-all duration-300 shadow-[0_0_8px_var(--glow-primary)]" style={{
                      width: `${Math.max(4, Math.min(100, val))}%`,
                      backgroundColor: `hsl(${cardHue} 100% 50%)`
                    }} />
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>
        </TabsContent>
      </Tabs>

      {/* Footer Controls: Copy, New Timeline, Close, Fiction Notice */}
      <div className="border-t border-border/70 pt-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <button type="button" onClick={onCopy} className="ql-btn min-h-[44px] text-xs justify-center" aria-label={`Copy profile narrative for ${name}`}>
              <Copy className="size-3.5 mr-1.5 shrink-0" />
              <span>Copy this profile</span>
            </button>

            <button type="button" onClick={onRegenerate} className="ql-btn-ghost min-h-[44px] text-xs justify-center" aria-label={`Generate a new variation for ${name}`}>
              <RefreshCw className="size-3.5 mr-1.5 shrink-0" />
              <span>New timeline variation</span>
            </button>
          </div>

          <button type="button" onClick={onClose} className="min-h-[44px] px-4 rounded-full text-xs font-mono text-muted-foreground hover:text-foreground cursor-pointer transition-colors text-center">
            Close Profile
          </button>
        </div>

        {/* Persistent Fiction Notice within Detail modal */}
        <p className="text-[11px] text-muted-foreground italic leading-relaxed text-center sm:text-left border-t border-border/40 pt-3">
          {FICTION_NOTICE}
        </p>
      </div>
    </div>;
}